import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React from 'react';

import { CREATE, DataAPIContext } from '../../api';
import { isBackgroundTaskResponse, notifyBackgroundTask } from '../../bg_tasks/batchResponseHelper';
import { BackgroundTaskContext } from '../../bg_tasks/BackgroundTaskProvider';
import useTaskCallback from '../../bg_tasks/useTaskCallback';
import RemoteAutocomplete from '../../components/RemoteAutocomplete';

const useStyles = makeStyles((theme) => ({
    BatchActionPaperWrapper: {
        width: '300px',
        zIndex: 100,
        marginRight: '1rem',
        marginTop: '0.5rem',
        height: '400px',
        padding: '1rem',
        position: 'fixed',
        right: '1rem',
        top: '12vh',
        backgroundColor: 'white',
        border: 'solid 1px #d8d8d8',
        borderRadius: '12px',
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
}));

const BatchActionsMenu = (props) => {
    const [tagsToAddOrRemove, setTagsToAddOrRemove] = React.useState([]);
    const [observedUsersToAddOrRemove, setObservedUsersToAddOrRemove] = React.useState([]);
    const { batchActionType, setBatchActionType, selectedIds, setSelectedIds, setRelatedLoading, setSyncActive, setShowActionForm, setLastUpdate, nonRequiredTripAssociatedTagsIds } = props;
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const bgCtx = React.useContext(BackgroundTaskContext);
    const onTaskDone = useTaskCallback();
    const dataAPI = React.useContext(DataAPIContext);
    const classes = useStyles();

    const tripAssociatedTagsIds = props.trip.tag_categories
        .filter((cat) => cat.category.code !== 'departure')
        .map((cat) => {
            return cat.category.id;
        });

    const addTagsToAddOrRemove = (ev, nt) => {
        if (nt) {
            setTagsToAddOrRemove((prevState) => [...prevState, nt]);
        }
    };

    const addObservedUsersToAddOrRemove = (ev, user) => {
        if (user) {
            setObservedUsersToAddOrRemove((prevState) => [...prevState, user]);
        }
    };

    const submitAction = () => {
        let params = {};
        if (batchActionType === 'ADD_TAGS') {
            params = {
                data: {
                    action: 'add_tags',
                    data: {
                        list: selectedIds,
                        tour: props.trip.id,
                        tags: tagsToAddOrRemove.map((tag) => {
                            return tag.id;
                        }),
                    },
                },
            };
        }

        if (batchActionType === 'REMOVE_TAGS') {
            params = {
                data: {
                    action: 'remove_tags',
                    data: {
                        list: selectedIds,
                        tour: props.trip.id,
                        tags: tagsToAddOrRemove.map((tag) => {
                            return tag.id;
                        }),
                    },
                },
            };
        }

        if (batchActionType === 'ADD_OBSERVED_USERS') {
            params = {
                data: {
                    action: 'add_observed_users',
                    data: {
                        list: selectedIds,
                        tour: props.trip.id,
                        observed_users: observedUsersToAddOrRemove.map((user) => {
                            return user.id;
                        }),
                    },
                },
            };
        }

        if (batchActionType === 'REMOVE_OBSERVED_USERS') {
            params = {
                data: {
                    action: 'remove_observed_users',
                    data: {
                        list: selectedIds,
                        tour: props.trip.id,
                        observed_users: observedUsersToAddOrRemove.map((user) => {
                            return user.id;
                        }),
                    },
                },
            };
        }

        params.urlSuffix = 'batch/';
        setRelatedLoading(true);
        setSyncActive(true);
        dataAPI(CREATE, 'tour_user', params)
            .then((res) => {
                if (isBackgroundTaskResponse(res)) {
                    notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                    setSelectedIds && setSelectedIds([]);
                    // Refresh grid when background task completes
                    onTaskDone(res.data.task_id, (task) => {
                        setLastUpdate(Date.now());
                    });
                } else if (res.statusCode === 200 || res.statusCode === 201) {
                    enqueueSnackbar('Users updated', { variant: 'success' });
                    setSelectedIds && setSelectedIds([]);
                    setLastUpdate(Date.now());
                } else {
                    enqueueSnackbar('Users update failed', { variant: 'error' });
                }
            })
            .catch((err) => {
                enqueueSnackbar('Error completing action', { variant: 'error' });
            })
            .finally(() => {
                setBatchActionType('');
                setTagsToAddOrRemove([]);
                setShowActionForm(false);
                setRelatedLoading(false);
                setSyncActive(false);
            });
    };

    const handleCloseActionForm = () => {
        setTagsToAddOrRemove([]);
        setObservedUsersToAddOrRemove([]);
        setBatchActionType('');
        setShowActionForm(false);
    };

    return (
        <div className={classes.BatchActionPaperWrapper}>
            <div style={{ maxHeight: '350px', overflow: 'auto' }}>
                {batchActionType === 'ADD_TAGS' && tripAssociatedTagsIds.length > 0 && (
                    <div>
                        <Typography style={{ fontWeight: 'bold' }}>ADD TAGS</Typography>
                        <RemoteAutocomplete
                            apiFilters={[{ field: 'category', op: 'eq', value: [...tripAssociatedTagsIds.map((id) => id.toString())] }]}
                            apiParams={{ ordering: 'category,name' }}
                            optionsResultsSize={200}
                            resource="tour_tag"
                            urlSuffix={null}
                            label="Select tag*"
                            variant="filled"
                            size="small"
                            getOptionLabel={(opt) => `${opt.category.name}: ${opt.name}`}
                            onChange={(ev, nt) => addTagsToAddOrRemove(ev, nt)}
                        />
                        {tagsToAddOrRemove.map((tag) => {
                            return <div style={{ padding: '0.5rem', margin: '0.5rem 0', border: 'solid 1px #d8d8d8' }}>{tag.name}</div>;
                        })}
                    </div>
                )}

                {batchActionType === 'REMOVE_TAGS' && tripAssociatedTagsIds.length > 0 && nonRequiredTripAssociatedTagsIds.length > 0 && (
                    <div>
                        <Typography style={{ fontWeight: 'bold' }}>REMOVE TAGS</Typography>
                        <RemoteAutocomplete
                            apiFilters={[{ field: 'category', op: 'eq', value: [...nonRequiredTripAssociatedTagsIds.map((id) => id.toString())] }]}
                            apiParams={{ ordering: 'category,name' }}
                            resource="tour_tag"
                            urlSuffix={null}
                            label="Select tag*"
                            variant="filled"
                            size="small"
                            getOptionLabel={(opt) => `${opt.category.name}: ${opt.name}`}
                            optionsResultsSize={200}
                            onChange={(ev, nt) => addTagsToAddOrRemove(ev, nt)}
                        />
                        {tagsToAddOrRemove.map((tag) => {
                            return <div style={{ padding: '0.5rem', margin: '0.5rem 0', border: 'solid 1px #d8d8d8' }}>{tag.name}</div>;
                        })}
                    </div>
                )}

                {batchActionType === 'ADD_OBSERVED_USERS' && (
                    <div>
                        <Typography style={{ fontWeight: 'bold' }}>ADD OBSERVED USERS</Typography>
                        <RemoteAutocomplete
                            resource="tour_user"
                            apiParams={{ tour_id: props.trip.id }}
                            urlSuffix={null}
                            label="Choose user"
                            variant="filled"
                            size="small"
                            onChange={(ev, user) => addObservedUsersToAddOrRemove(ev, user)}
                            getOptionLabel={(opt) => (opt && opt.user ? opt.user.display_name : '')}
                        />
                        {observedUsersToAddOrRemove.map((user) => {
                            return <div style={{ padding: '0.5rem', margin: '0.5rem 0', border: 'solid 1px #d8d8d8' }}>{user.user.email}</div>;
                        })}
                    </div>
                )}

                {batchActionType === 'REMOVE_OBSERVED_USERS' && (
                    <div>
                        <Typography style={{ fontWeight: 'bold' }}>REMOVE OBSERVED USERS</Typography>
                        <RemoteAutocomplete
                            resource="tour_user"
                            apiParams={{ tour_id: props.trip.id }}
                            urlSuffix={null}
                            label="Choose user"
                            variant="filled"
                            size="small"
                            onChange={(ev, user) => addObservedUsersToAddOrRemove(ev, user)}
                            getOptionLabel={(opt) => (opt && opt.user ? opt.user.display_name : '')}
                        />
                        {observedUsersToAddOrRemove.map((user) => {
                            return <div style={{ padding: '0.5rem', margin: '0.5rem 0', border: 'solid 1px #d8d8d8' }}>{user.user.email}</div>;
                        })}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', position: 'absolute', bottom: '1rem', left: '1rem', right: '1rem' }}>
                <Button variant="contained" size="small" color="primary" onClick={() => submitAction(batchActionType)}>
                    Submit
                </Button>
                <Button variant="outlined" size="small" color="primary" onClick={() => handleCloseActionForm()}>
                    Cancel
                </Button>
            </div>
        </div>
    );
};

export default BatchActionsMenu;
